export const DESCRIPTION_PREVIEW_LENGTH = 140;

const CLASSES_VIEWS = ['classes-day-view', 'classes-list-view', 'classes-week-view'];

type Scope = {
  $$phase?: unknown;
  $root?: { $$phase?: unknown };
  $apply: (callback: () => void) => void;
};

type BookDialogDependencies = {
  translate: (key: string) => string;
  ordinal: (value: number) => string;
  getCurrentBranch: () => { _id?: string } | null | undefined;
  getCurrentPath: () => string;
  goToState: (state: string, params: { branchId?: string }) => void;
  eligibilityMessageType: unknown;
};

function hasCurrentBranch(
  branch: { _id?: string } | null | undefined
): branch is { _id?: string } {
  if (!branch) {
    return false;
  }
  return Object.keys(branch).length > 0;
}

function currentClassesView(path: string): string {
  return path.split('/')[3] || '';
}

export function createBookDialogService(dependencies: BookDialogDependencies) {
  function currentBranch() {
    return dependencies.getCurrentBranch();
  }

  function forceReloadClassesView() {
    const branch = currentBranch();
    if (!hasCurrentBranch(branch)) {
      return;
    }
    if (!CLASSES_VIEWS.includes(currentClassesView(dependencies.getCurrentPath()))) {
      return;
    }
    dependencies.goToState('classes', { branchId: branch._id });
  }

  function goToMemberships() {
    const branch = currentBranch();
    if (!hasCurrentBranch(branch)) {
      return;
    }
    dependencies.goToState('memberships', { branchId: branch._id });
  }

  function buildNotEligibleDialogData(closeBookDialog?: () => void) {
    return {
      type: dependencies.eligibilityMessageType,
      title: 'WEB_ELIGIBILITY_TO_BOOK',
      message: 'WEB_SORRY_YOU_ARE_NOT_ELIGIBLE_TO_BOOK_THIS_EVENT',
      buttonLabel: 'WEB_VIEW_MEMBERSHIPS_AND_CREDITS',
      action: () => {
        closeBookDialog?.();
        goToMemberships();
      },
    };
  }

  function buildDescriptionData(event?: { description?: string }) {
    return {
      description: event?.description || '',
      previewLength: DESCRIPTION_PREVIEW_LENGTH,
    };
  }

  function buildScheduleDays(
    event: { type?: string; scheduleToBook?: { days?: unknown[] } } | null | undefined,
    formatters: {
      getDayLabel?: (day: unknown) => string;
      getTimeRange?: (day: unknown) => string;
      getTrainers?: (day: unknown) => string;
    } = {}
  ) {
    if (event?.type !== 'course' || !event.scheduleToBook?.days) {
      return [];
    }
    return event.scheduleToBook.days.map((day) => ({
      dayLabel: formatters.getDayLabel?.(day) || '',
      timeRange: formatters.getTimeRange?.(day) || '',
      trainers: formatters.getTrainers?.(day) || '',
    }));
  }

  function buildSummaryData(ctx: any = {}) {
    if (!ctx?.event) {
      return {};
    }
    const event = ctx.event;
    const showTimeRow = event.type !== 'course';
    const duration = ctx.duration == null ? '' : ctx.duration;
    const waitingPositionLine =
      ctx.autoBookingIsEnabled && event.position
        ? `${dependencies.ordinal(event.position)} ${dependencies.translate('WEB_ON_WAITING_LIST')}`
        : null;

    return {
      eventDate: ctx.eventDate || '',
      showTimeRow,
      timeRow: showTimeRow
        ? `${ctx.startTime || ''} - ${duration} ${dependencies.translate('WEB_MINUTES')}`
        : '',
      showTimeslotMeta: event.type !== 'timeslot',
      bookingsLine: ctx.bookingsLine || '',
      showWaitingListBlock:
        !!ctx.showWaitingList && event.type !== 'course' && !!ctx.isGoingToWaitingList,
      waitingListLine: ctx.waitingListLine || '',
      waitingPositionLine,
      levelLine: ctx.showLevel ? event.level : null,
      trainersLine: ctx.trainersLine == null ? null : ctx.trainersLine,
      facilityLine: ctx.showFacilitiesNames ? ctx.facilityName || '' : null,
      showCourseSchedule: event.type === 'course',
      viewScheduleLabel: dependencies.translate('WEB_VIEW_SCHEDULE'),
      scheduleDays: ctx.scheduleDays || [],
      showAttendancePolicy: !!ctx.showAttendancePolicy,
      attendancePolicyTitle: dependencies.translate('WEB_ATTENDANCE_POLICY'),
      attendancePolicyMessage: ctx.attendancePolicyMessage || '',
    };
  }

  function buildGuestCounterData(options: any = {}) {
    const guests = options.guests || 0;
    return {
      guests,
      label: options.label || '',
      decreaseDisabled:
        options.decreaseDisabled !== undefined ? !!options.decreaseDisabled : guests <= 0,
      countId: options.countId || '',
      increaseId: options.increaseId || '',
      decreaseId: options.decreaseId || '',
    };
  }

  function runInDigest(scope: Scope, callback: () => void) {
    if (scope.$$phase || scope.$root?.$$phase) {
      callback();
    } else {
      scope.$apply(callback);
    }
  }

  function buildTermsData(options: any = {}) {
    const state = options.state || {};
    const items =
      options.mode === 'payg'
        ? [buildPaygItem(state)]
        : options.mode === 'membership'
          ? buildMembershipItems(state)
          : [];
    return { mode: options.mode || '', items };
  }

  return {
    DESCRIPTION_PREVIEW_LENGTH,
    buildDescriptionData,
    buildScheduleDays,
    buildSummaryData,
    buildGuestCounterData,
    buildTermsData,
    runInDigest,
    forceReloadClassesView,
    goToMemberships,
    buildNotEligibleDialogData,
  };
}

function buildPaygItem(state: any) {
  return {
    key: 'payg',
    accepted: !!state.accepted,
    agreeLabelKey: state.agreeLabelKey || 'WEB_I_AGREE_TO',
    linkLabelKey: state.linkLabelKey || 'WEB_PAYG_PAYMENT_TERMS',
    checkboxId: state.checkboxId || 'class-booking-accept-terms',
    onChange: state.onChange,
    onClickLink: state.onClickLink,
  };
}

function isPurchaseTermsForced(state: any) {
  if (!state.isFtcEnabled) {
    return false;
  }
  return !state.isCreditPack;
}

function isAutorenewalTermsForced(state: any) {
  return !state.isCustomLink;
}

function shouldShowPurchaseTerms(state: any) {
  if (!state.isCustomLink) {
    return true;
  }
  return !state.isFtcEnabled;
}

function shouldShowAutorenewalTerms(state: any) {
  if (state.isSingleMembership) {
    return false;
  }
  if (state.isCreditPack) {
    return false;
  }
  return !!state.isFtcEnabled;
}

function autorenewalAgreeLabelKey(state: any) {
  if (state.isCustomLink) {
    return 'WEB_FTC_CONSENT_TO_AUTORENEWAL_TERMS';
  }
  return 'WEB_I_AGREE_AUTORENEWAL_CS';
}

function buildPurchaseTermsItem(state: any) {
  const forced = isPurchaseTermsForced(state);
  return {
    key: 'purchase',
    accepted: forced || !!state.termsAccepted,
    disabled: forced,
    agreeLabelKey: state.agreeLabelKey || 'WEB_I_AGREE_TO',
    linkLabelKey: state.linkLabelKey || 'WEB_MEMBERSHIP_PURCHASE_TERMS',
    checkboxId: state.checkboxId || 'purchase-membership-accept-terms',
    onChange: state.onPurchaseChange,
    onClickLink: state.onClickLink,
  };
}

function buildAutorenewalTermsItem(state: any) {
  const forced = isAutorenewalTermsForced(state);
  return {
    key: 'autorenewal',
    accepted: forced || !!state.autoRenewalTermsAccepted,
    disabled: forced,
    agreeLabelKey: autorenewalAgreeLabelKey(state),
    onChange: state.onAutorenewalChange,
  };
}

function buildMembershipItems(state: any) {
  const items = [];
  if (shouldShowPurchaseTerms(state)) {
    items.push(buildPurchaseTermsItem(state));
  }
  if (shouldShowAutorenewalTerms(state)) {
    items.push(buildAutorenewalTermsItem(state));
  }
  return items;
}
